import { useAppDispatch, useAppTranslation } from "@/utils";
import { useTransferLabel } from "@/utils/DigitalAccount/useTransferLabel";
import {
  Bank,
  Button,
  Checkbox,
  Col,
  Flex,
  Input,
  InputNumber,
  LegacyCard,
  LegacyForm,
  Modal,
  Row,
  Select,
  Space,
  Typography,
  useMessagedAsyncFn,
  usePaypingContext,
} from "@payping/web-components";
import React, { Key } from "react";
import { ValidSheba } from "@payping/utils/validation";
import { addComma, isAlphabet } from "@payping/utils/string";
import { setSamanRequestList, setSamanUpdateRequestList } from "@payping/redux/actions";
import { CuToman, ExclamationCircleOutlined } from "@payping/icons";
import type { FormInstance, FormListFieldData } from "@payping/web-components/legacyComponents/Form/types";
import { SamanRowType } from "@/pages/DigitalAccount/Saman/Create/SecondStep/index";
import { deleteId, editId } from "@/assets/googleTrackIds";
import { maxTransferSaman, minTransferSaman } from "@/pages/DigitalAccount/TransferLimit";
import { BatchPaymentDetailsReasonCodeEnum } from "@payping/api";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { getBankInfoFromSheba } from "@payping/utilities-shared/bank";

interface Props {
  fields: FormListFieldData[];
  form: FormInstance<SamanRowType>;
}

const TransferList: React.FC<Props> = ({ form, fields }) => {
  const { t } = useAppTranslation();
  const [modal, contextHolder] = Modal.useModal();
  const dispatch = useAppDispatch();
  const { breakpoints } = usePaypingContext();
  const { lg } = breakpoints;
  const { getLabelTransfer, getLabelReasonTransfer } = useTransferLabel();

  const editCard = (key: number) => {
    const rowValues = form.getFieldValue("transfer")[key];

    const transferList = form.getFieldValue("transfer");
    transferList[key].isEditing = true;
    transferList[key].changeIban = rowValues.iban;
    transferList[key].changeAmount = rowValues.amount / 10;
    transferList[key].changeDescription = rowValues.description;
    transferList[key].changeReasonCode = rowValues.reasonCode;
    form.setFieldValue("transfer", transferList);
  };

  const editCancel = (key: Key) => {
    form.setFieldValue(
      "transfer",
      form
        .getFieldValue("transfer")
        ?.map?.((transfer: SamanRowType, index: number) =>
          index === key ? { ...transfer, isEditing: false } : transfer,
        ),
    );
  };

  const shabaValidation = (value: string) => {
    if (value.includes("IR")) return !ValidSheba(value);
    return !ValidSheba(`IR${value}`);
  };

  const [{ loading: loadingSubmit }, saveEdit] = useMessagedAsyncFn(
    async (key) => {
      await form.validateFields();
      const rowValues = form.getFieldValue("transfer")[key];
      const transferList = form.getFieldValue("transfer");
      transferList[key].iban = rowValues.changeIban;
      transferList[key].amount = rowValues.changeAmount * 10;
      transferList[key].description = rowValues.changeDescription;
      transferList[key].reasonCode = rowValues.changeReasonCode;
      if (transferList[key].amount) {
        await dispatch(
          setSamanRequestList({
            operationDetails: (transferList || []).map((item: SamanRowType) => {
              const correctIRiban = item.iban?.includes("IR") ? item.iban : `IR${item.iban}`;

              return {
                amount: item.amount ? Number(item.amount) / 10 : undefined,
                description: item.description,
                iban: correctIRiban,
                reasonCode: item.reasonCode,
              };
            }),
          }),
        );
      }
    },
    [dispatch, form],
    undefined,
    { useProgress: true },
  );

  const [, handleDelete] = useMessagedAsyncFn(
    async (record: SamanRowType) => {
      const transferList = form.getFieldValue("transfer");
      modal.confirm({
        cancelText: t("cancel"),
        content: t("message.remove", { subject: t("common.transfer") }),
        mask: { closable: true },
        okText: t("ok"),
        onOk: async () => {
          await dispatch(
            setSamanUpdateRequestList({
              paymentDetailsModels: transferList.filter((item: SamanRowType) => record.id !== item.id),
            }),
          );
        },
      });
    },
    [dispatch, form, modal, t],
    undefined,
    { useProgress: true },
  );

  return (
    <>
      <Flex vertical gap={16} className={"tw:w-full"}>
        {fields.map(({ key, name, ...restField }) => (
          <LegacyCard.Ant key={key}>
            <LegacyForm.Item hidden valuePropName={"checked"} {...restField} name={[name, "isEditing"]}>
              <Checkbox />
            </LegacyForm.Item>
            <LegacyForm.Item
              className={"tw:mb-0!"}
              shouldUpdate={(prev, next) => prev.transfer?.[key]?.isEditing !== next.transfer?.[key]?.isEditing}
            >
              {({ getFieldValue }) => {
                const rowValues = getFieldValue("transfer")[key];

                return rowValues?.isEditing ? (
                  <Row gutter={[8, 16]}>
                    <Col xs={24} lg={11}>
                      <LegacyForm.Item
                        className={"tw:mb-0!"}
                        {...restField}
                        name={[name, "changeIban"]}
                        required={true}
                        rules={[
                          { required: true },
                          {
                            validator(_, value) {
                              if (value && shabaValidation(value)) {
                                return Promise.reject(new Error(t("settlement.error.shabaValidation")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          placeholder={t("digitalAccount.samanCreate.shebaInput")}
                          addonAfter={"IR"}
                          onPressEnter={(event) => {
                            event.preventDefault();
                          }}
                        />
                      </LegacyForm.Item>
                    </Col>
                    <Col xs={24} lg={11}>
                      <LegacyForm.Item
                        className={"tw:mb-0!"}
                        {...restField}
                        name={[name, "changeAmount"]}
                        required={true}
                        rules={[
                          { required: true },
                          {
                            validator: (_, value): Promise<void> => {
                              const val = Number(value);
                              return value && (val < minTransferSaman || val > maxTransferSaman)
                                ? Promise.reject(
                                    t("settlement.error.amount", {
                                      max: addComma(maxTransferSaman),
                                      min: minTransferSaman,
                                    }),
                                  )
                                : Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber
                          className="tw:w-full!"
                          suffix={<CuToman width={24} height={24} />}
                          inputMode="numeric"
                          changeOnWheel={false}
                          controls={false}
                          placeholder={t("digitalAccount.samanCreate.amountInput")}
                          onPressEnter={(event) => {
                            event.preventDefault();
                          }}
                        />
                      </LegacyForm.Item>
                    </Col>
                    <Col xs={24} lg={11}>
                      <LegacyForm.Item className={"tw:mb-0!"} {...restField} name={[name, "changeReasonCode"]}>
                        <Select
                          placeholder={t("digitalAccount.samanCreate.selectReason")}
                          allowClear
                          className={"tw:w-full"}
                          options={[
                            {
                              label: t("digitalAccount.samanCreate.depositOfCollateral"),
                              value: BatchPaymentDetailsReasonCodeEnum.CCPA,
                            },
                            {
                              label: t("digitalAccount.samanCreate.customsDuties"),
                              value: BatchPaymentDetailsReasonCodeEnum.CDAP,
                            },
                            {
                              label: t("digitalAccount.samanCreate.cashAndCharityDonations"),
                              value: BatchPaymentDetailsReasonCodeEnum.CPAC,
                            },
                            {
                              label: t("digitalAccount.samanCreate.debtPayment"),
                              value: BatchPaymentDetailsReasonCodeEnum.DRPA,
                            },
                            {
                              label: t("digitalAccount.samanCreate.otherMatters"),
                              value: BatchPaymentDetailsReasonCodeEnum.FRTX,
                            },
                            {
                              label: t("digitalAccount.samanCreate.currencyAffairs"),
                              value: BatchPaymentDetailsReasonCodeEnum.FXAP,
                            },
                            {
                              label: t("digitalAccount.samanCreate.buyingGoodsAndServices"),
                              value: BatchPaymentDetailsReasonCodeEnum.GASP,
                            },
                            {
                              label: t("digitalAccount.samanCreate.otherGovernmentServices"),
                              value: BatchPaymentDetailsReasonCodeEnum.GEAC,
                            },
                            {
                              label: t("digitalAccount.samanCreate.generalExpensesAndDailyAffairs"),
                              value: BatchPaymentDetailsReasonCodeEnum.GPAC,
                            },
                            {
                              label: t("digitalAccount.samanCreate.medicalAffairs"),
                              value: BatchPaymentDetailsReasonCodeEnum.HIPA,
                            },
                            {
                              label: t("digitalAccount.samanCreate.realEstateTransactions"),
                              value: BatchPaymentDetailsReasonCodeEnum.IMPT,
                            },
                            {
                              label: t("digitalAccount.samanCreate.serviceInsuranceAffairs"),
                              value: BatchPaymentDetailsReasonCodeEnum.IOSP,
                            },
                            {
                              label: t("digitalAccount.samanCreate.investmentAndStockMarketAffairs"),
                              value: BatchPaymentDetailsReasonCodeEnum.ISAP,
                            },
                            {
                              label: t("digitalAccount.samanCreate.liquidityManagement"),
                              value: BatchPaymentDetailsReasonCodeEnum.LMAP,
                            },
                            {
                              label: t("digitalAccount.samanCreate.facilityRepayment"),
                              value: BatchPaymentDetailsReasonCodeEnum.LRPA,
                            },
                            {
                              label: t("digitalAccount.samanCreate.movablePropertyTransactions"),
                              value: BatchPaymentDetailsReasonCodeEnum.MPTP,
                            },
                            {
                              label: t("digitalAccount.samanCreate.salaryDeposit"),
                              value: BatchPaymentDetailsReasonCodeEnum.POSA,
                            },
                            {
                              label: t("digitalAccount.samanCreate.retirementAffairs"),
                              value: BatchPaymentDetailsReasonCodeEnum.RTAP,
                            },
                            {
                              label: t("digitalAccount.samanCreate.taxSettlement"),
                              value: BatchPaymentDetailsReasonCodeEnum.TCAP,
                            },
                          ]}
                        ></Select>
                      </LegacyForm.Item>
                    </Col>
                    <Col xs={24} lg={11}>
                      <LegacyForm.Item
                        className={"tw:mb-0!"}
                        {...restField}
                        name={[name, "changeDescription"]}
                        rules={[
                          {
                            validator(_, value) {
                              if (!isAlphabet(value, { withNumber: true }))
                                return Promise.reject(new Error(t("settlement.error.descriptionException")));
                              if (value && value.length > 30)
                                return Promise.reject(new Error(t("settlement.error.description")));
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input.TextArea
                          placeholder={t("digitalAccount.samanCreate.descriptionInput")}
                          className="tw:leading-8"
                        />
                      </LegacyForm.Item>
                    </Col>
                    <Col xs={24} lg={2}>
                      <Row gutter={[8, 8]}>
                        <Col xs={24} sm={18} lg={24}>
                          {lg ? (
                            <Button block onClick={() => editCancel(key)}>
                              {t("cancel")}
                            </Button>
                          ) : (
                            <Button block type={"primary"} onClick={() => saveEdit(key)} loading={loadingSubmit}>
                              {t("add.add")}
                            </Button>
                          )}
                        </Col>
                        <Col xs={24} sm={6} lg={24}>
                          {lg ? (
                            <Button block type={"primary"} onClick={() => saveEdit(key)} loading={loadingSubmit}>
                              {t("add.add")}
                            </Button>
                          ) : (
                            <Button block onClick={() => editCancel(key)}>
                              {t("cancel")}
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                ) : (
                  <Row className={"tw:flex tw:w-full tw:items-center tw:justify-between"} gutter={[8, 8]}>
                    <Col xl={18} lg={24} xs={24}>
                      <Row>
                        {!rowValues?.errors ? (
                          <Col xs={24}>
                            <Flex gap={"small"}>
                              <Typography.Title level={5}>{`${rowValues?.fullName}`}</Typography.Title>
                              <Typography.Text>{getLabelTransfer(rowValues?.transferMethod)}</Typography.Text>
                            </Flex>
                          </Col>
                        ) : null}
                        <Col xs={24}>
                          <Row>
                            <Col md={10} sm={24} xs={24}>
                              <Space>
                                <Typography.Text type={"secondary"}>{rowValues?.iban}</Typography.Text>
                                <Bank.Icon showTooltip bankName={getBankInfoFromSheba(rowValues?.iban ?? "")?.name} />
                              </Space>
                            </Col>
                          </Row>
                        </Col>
                        <Col xs={24}>
                          <Flex gap={"small"}>
                            <Typography.Text type={"secondary"}>
                              {getLabelReasonTransfer(rowValues?.reasonCode)}
                            </Typography.Text>
                            <Typography.Text ellipsis type={"secondary"}>
                              {rowValues?.description}
                            </Typography.Text>
                          </Flex>
                        </Col>
                        {rowValues?.errors ? (
                          <Col xs={24}>
                            <Space>
                              <ExclamationCircleOutlined width={14} className="color--danger tw:flex tw:items-center" />
                              <Typography.Text>{rowValues?.errors}</Typography.Text>
                            </Space>
                          </Col>
                        ) : null}
                      </Row>
                    </Col>
                    <Col xl={6} lg={24} xs={24}>
                      <Flex justify="space-between" align="center" className="tw:w-full">
                        <Flex align="center">
                          <Typography.Text>{`${rowValues?.amount ? formatLocalizedNumber({ value: rowValues.amount / 10 }) : "0"}`}</Typography.Text>
                          <CuToman width={24} height={24} />
                        </Flex>
                        <div>
                          <Button type={"link"} onClick={() => handleDelete(rowValues)} data-google-track={deleteId}>
                            {t("common.remove")}
                          </Button>
                          <Button type={"link"} onClick={() => editCard(key)} data-google-track={editId}>
                            {t("common.edit")}
                          </Button>
                        </div>
                      </Flex>
                    </Col>
                  </Row>
                );
              }}
            </LegacyForm.Item>
          </LegacyCard.Ant>
        ))}
      </Flex>
      {contextHolder}
    </>
  );
};

export default TransferList;
